import './DodScheduleRunBuilder.scss';
import React, {ReactNode, useEffect, useState} from 'react';
import {DeliveryGroupType, DodSchedule} from '@/types/DodRun';
import {
    ByzzerChangeEventHandler,
    ByzzerSelect,
    ByzzerSelectOption,
    ByzzerSelectValueTypes,
} from '@byzzer/ui-components';
import {useUser} from '@/contexts/UserContext';
import DodSchedulePicker from './DodSchedulePicker/DodSchedulePicker';
import {
    DodSchedulePreview
} from "@/components/DodConfigEditor/builders/DodScheduleRunBuilder/DodSchedulePreview/DodSchedulePreview";
import classnames from "classnames";
import { isValidArray } from '@/utils';

const baseClassName = 'dod-schedule-run-builder';

export type DodScheduleRunBuilderProps = {
    className?: string;
    name?: string;
    value: DodSchedule;
    tip?: ReactNode;
    onChange: ByzzerChangeEventHandler<DodSchedule>;
    onValidityChange?(e: ByzzerValidityChangeEvent): void;
} & Partial<Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>>;

type RepetitionOption = {
    display: string;
    value: DeliveryGroupType;
};

const NOTIFY_OPTIONS: RepetitionOption[] = [
    {
        display: 'Only Me',
        value: 'me',
    },
    {
        display: 'My Team',
        value: 'team',
    },
    {
        display: 'Everyone on my Subscription',
        value: 'everyone',
    },
    {
        display: 'Pick By Member',
        value: 'custom',
    },
];

export function DodScheduleRunBuilder({
                                          className,
                                          name,
                                          onChange,
                                          onValidityChange,
                                          value,
                                          tip,
                                          ...props
                                      }: DodScheduleRunBuilderProps) {
    const [deliveryGroupType] = useState<DeliveryGroupType>(NOTIFY_OPTIONS[0].value);
    const [userOptions, setUserOptions] = useState<ByzzerSelectOption[]>([]);
    const {teamMembers: allUsersInCompany, user} = useUser();

    useEffect(() => {
      const userIds = getSelectedUserIds(value.deliveryGroupType || deliveryGroupType)
      if(!isValidArray(value.deliverToUserIds)) {
            onChange?.({
                value: {
                    ...value,
                    deliverToUserIds: userIds,
                },
            });
        }
    }, [])

    function getSelectedUserIds(deliveryGroupType: DeliveryGroupType) {
        switch (deliveryGroupType) {
            case 'me':
                setUserOptions([{display: `${user.firstName} ${user.lastName}`, value: user.id}]);
                return [user.id];
            case 'custom':
                const members = allUsersInCompany.filter((member) => member.type === "user").map((user) => {
                    return {display: `${user.firstName} ${user.lastName}`, value: user.id};
                });
                setUserOptions(members);
                return [];
            case 'team':
                const team = allUsersInCompany
                    .filter((member) => member.teamId === user.teamId && member.type === "user")
                    .map((user) => {
                        return {display: `${user.firstName} ${user.lastName}`, value: user.id};
                    });
                setUserOptions(team);
                return team.map((member) => member.value);
            case 'everyone':
                const everyone = allUsersInCompany.filter((member) => member.type === "user").map((user) => {
                    return {display: `${user.firstName} ${user.lastName}`, value: user.id};
                });
                setUserOptions(everyone);
                return everyone.map((member) => member.value);
            default:
                return [];
        }
    }

    function handleDeliveryGroupTypeChange(e: ByzzerChangeEvent<ByzzerSelectValueTypes>) {
        const deliveryGroupType = e.value as DeliveryGroupType
        const userIds = getSelectedUserIds(deliveryGroupType);
        onChange?.({
            value: {
                ...value,
                deliveryGroupType: e.value as DeliveryGroupType,
                deliverToUserIds: userIds,
            },
        });
    }

    function handleDeliveryMemberChange(e: ByzzerSelectOption[]) {
        onChange?.({
            value: {
                ...value,
                // @ts-ignore userIds: number[]
                deliverToUserIds: e.value,
            },
        });
    }

    return (
        <div className={classnames(baseClassName, className)} {...props}>
            <DodSchedulePicker value={value} onChange={onChange} className={`${baseClassName}__timepicker-section`}/>
            <DodSchedulePreview schedule={value} className={`${baseClassName}__delivery-schedule`}/>
            <div className={`${baseClassName}__notify-section`}>
                <ByzzerSelect
                    label="Who do you want to notify each time the run is ready?"
                    value={value.deliveryGroupType}
                    options={NOTIFY_OPTIONS}
                    allowClear={false}
                    onChange={handleDeliveryGroupTypeChange}
                />
                {value.deliveryGroupType === 'custom' && (
                    <ByzzerSelect
                    label="Selected Members"
                    value={value.deliverToUserIds}
                    options={userOptions}
                    onChange={handleDeliveryMemberChange}
                    allowMultiple={true}
                />)}
            </div>
        </div>
    );
}

export default DodScheduleRunBuilder;
